import { useCallback, useMemo, useState } from "react";

export interface PaginationData {
  page?: number;
  pageSize?: number;
}
interface UpdateData<T> {
  newInitialData?: T[];
  newPage?: number;
  newPageSize?: number;
}

interface UsePaginationResult<T> {
  data: T[];
  goToPage: (page: number) => void;
  goToNext: () => void;
  goToPrev: (page: number) => void;
  updatePageData: (update: UpdateData<T>) => void;
  currentPageData: PaginationData & {
    numPages: number;
    totalResults: number;
  };
}
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

/**
 * Hook used for paginating data on Relationships graph on the client side.
 * @param initialData List of objects that needs to be paginated.
 * @param options settings for pagination
 */
const usePagination = <T>(
  initialData: T[],
  options: PaginationData = {}
): UsePaginationResult<T> => {
  const [currentPage, setCurrentPage] = useState<number>(
    options.page || DEFAULT_PAGE
  );
  const [pageSize, setPageSize] = useState<number>(
    options.pageSize || DEFAULT_PAGE_SIZE
  );

  const slicedDataMemo = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return initialData.slice(startIndex, endIndex); // memo used because slice rets new ref
  }, [initialData, currentPage, pageSize]);

  const totalResults = initialData?.length ?? 0;
  const totalPages = Math.ceil(totalResults / pageSize);

  const currentPageData = useMemo(
    () => ({
      page: currentPage,
      pageSize,
      numPages: totalPages,
      totalResults
    }),
    [currentPage, pageSize, totalPages, totalResults]
  );

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
    },
    [setCurrentPage, totalPages]
  );

  const goToNext = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const goToPrev = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const updatePageData = ({ newPage, newPageSize }: UpdateData<T>) => {
    setCurrentPage(newPage || DEFAULT_PAGE);
    setPageSize(newPageSize || DEFAULT_PAGE_SIZE);
  };

  return {
    data: slicedDataMemo,
    goToPage,
    goToNext,
    goToPrev,
    updatePageData,
    currentPageData
  };
};
export default usePagination;
